import { useI18nForVue2 } from '@invivodf/locales/src/useI18n'

import {
  ErrorInfo,
  ImportReport,
  ImportReportContent,
  ImportReportLineError,
} from '@invivodf/common/src/contexts/parameters'
import {
  RecommendedListsImportReportSummaryDetailsFileErrorCodes,
  RecommendedListsImportReportSummaryDetailsLineErrorCodes,
  RecommendedListImportReportSummary,
  RecommendedListsImportReportSummaryDetails,
} from '@invivodf/wishlist-sdk'
import { ImportReportSummary } from '@invivodf/common/src/contexts/parameters/domain/ImportReport'

import { translateTypeFields } from '../translate-type.fields'

export const normalizeRecommendedListsReportSummary = (
  reportSummary: RecommendedListImportReportSummary,
): ImportReportSummary => ({
  ...reportSummary,
  imported_at: reportSummary.updated_at,
  translated_type: translateTypeFields(reportSummary.type),
  cooperative_id: 0,
  correlation_file_id: '',
  file_error_count: 0,
  file_url: '',
  manual_import: false,
  user_email: null,
  user_id: null,
})

export const normalizeRecommendedListsReport = (report: RecommendedListsImportReportSummaryDetails): ImportReport => {
  return {
    ...normalizeRecommendedListsReportSummary(report),
    report: mapImportReportContent(report.report),
  }
}

function mapImportReportContent(report: RecommendedListsImportReportSummaryDetails['report']): ImportReportContent {
  return {
    ...report,
    line_errors: mapLineErrors(report.line_errors),
    file_errors: mapFileErrors(report.file_errors),
  }
}

function mapFileErrors(fileErrors: RecommendedListsImportReportSummaryDetails['report']['file_errors']): string[] {
  if (!fileErrors || fileErrors.length === 0) {
    return []
  }

  return fileErrors.map((error) => mapErrorCodeToMessageTranslated(error))
}

function mapLineErrors(
  lineErrors: RecommendedListsImportReportSummaryDetails['report']['line_errors'],
): ImportReportLineError[] {
  return lineErrors.map(({ errors, ...lineError }) => {
    if (errors && errors.length > 0) {
      const messagesFromErrorCodes = errors.map((error) => mapErrorCodeToMessageTranslated(error))

      return {
        ...lineError,
        messages: messagesFromErrorCodes,
      }
    }

    return { ...lineError, messages: [] }
  })
}

function mapErrorCodeToMessageTranslated(error: ErrorInfo): string {
  const i18n = useI18nForVue2()
  const errorCode = error.code

  const translationKey = ERROR_CODE_TRANSLATIONS[errorCode]
  if (translationKey) {
    return translateErrorMessage(error, translationKey, i18n)
  }

  return i18n.t('ctx.import.errors__generic_error', { code: errorCode }).toString()
}

function translateErrorMessage(error: ErrorInfo, translationKey: string, i18n: any): string {
  const translationParams = {
    ...error.data,
    missingHeaders: error.data?.missingHeaders?.join(', '),
    allowedValues: error.data?.allowedValues?.join(', '),
  }

  return i18n.t(translationKey, translationParams).toString()
}

const IMPORT_ERROR_CODES = [
  ...Object.values(RecommendedListsImportReportSummaryDetailsFileErrorCodes),
  ...Object.values(RecommendedListsImportReportSummaryDetailsLineErrorCodes),
] as const

const ERROR_CODE_TRANSLATIONS: Record<string, string> = Object.fromEntries(
  IMPORT_ERROR_CODES.map((code) => [code, generateTranslationKey(code)]),
)

function generateTranslationKey(errorCode: string): string {
  return `ctx.import.errors__${errorCode.toLowerCase()}`
}
